import api from './api';

const inventoryService = {
    // CATEGORIES
    getCategories: async (params = {}) => {
        try {
            const response = await api.get('/api/inventory/categories', { params });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    createCategory: async (categoryData) => {
        try {
            const response = await api.post('/api/inventory/categories', categoryData);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    updateCategory: async (id, categoryData) => {
        try {
            const response = await api.put(`/api/inventory/categories/${id}`, categoryData);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    deleteCategory: async (id) => {
        try {
            await api.delete(`/api/inventory/categories/${id}`);
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    // SUPPLIERS
    getSuppliers: async (params = {}) => {
        try {
            const response = await api.get('/api/inventory/suppliers', { params });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    createSupplier: async (supplierData) => {
        try {
            const response = await api.post('/api/inventory/suppliers', supplierData);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    updateSupplier: async (id, supplierData) => {
        try {
            const response = await api.put(`/api/inventory/suppliers/${id}`, supplierData);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    deleteSupplier: async (id) => {
        try {
            await api.delete(`/api/inventory/suppliers/${id}`);
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    // PRODUCTS
    getProducts: async (params = {}) => {
        try {
            const response = await api.get('/api/inventory/products', { params });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    getProduct: async (id) => {
        try {
            const response = await api.get(`/api/inventory/products/${id}`);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    createProduct: async (productData) => {
        try {
            const response = await api.post('/api/inventory/products', productData);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    updateProduct: async (id, productData) => {
        try {
            const response = await api.put(`/api/inventory/products/${id}`, productData);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    deleteProduct: async (id) => {
        try {
            await api.delete(`/api/inventory/products/${id}`);
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    // INVENTORY MOVEMENTS
    getProductMovements: async (productId) => {
        try {
            const response = await api.get(`/api/inventory/products/${productId}/movements`);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    getAllMovements: async (params = {}) => {
        try {
            const response = await api.get('/api/inventory/movements', { params });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    adjustInventory: async (productId, adjustmentData) => {
        try {
            const response = await api.post(`/api/inventory/products/${productId}/adjust`, adjustmentData);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    // UTILITY METHODS
    getLowStockProducts: async () => {
        try {
            const productsResponse = await inventoryService.getProducts({ limit: 1000 });
            const products = productsResponse.data || [];
            return products.filter(product => 
                product.quantity_in_stock <= product.minimum_stock_level && 
                product.is_active
            );
        } catch (error) {
            throw error;
        }
    },

    getInventoryStats: async () => {
        try {
            // Get all data without pagination limits for accurate stats
            const [productsResponse, categoriesResponse, suppliersResponse] = await Promise.all([
                inventoryService.getProducts({ limit: 1000 }), // Large limit to get all
                inventoryService.getCategories({ limit: 1000 }),
                inventoryService.getSuppliers({ limit: 1000 })
            ]);
            
            // Extract data arrays from paginated responses
            const products = productsResponse.data || [];
            const categories = categoriesResponse.data || [];
            const suppliers = suppliersResponse.data || [];
            
            const activeProducts = products.filter(p => p.is_active);
            const lowStockProducts = products.filter(p => 
                p.quantity_in_stock <= p.minimum_stock_level && p.is_active
            );
            const outOfStockProducts = products.filter(p => 
                p.quantity_in_stock === 0 && p.is_active
            );
            
            const totalValue = activeProducts.reduce((sum, product) => 
                sum + (product.quantity_in_stock * product.unit_price), 0
            );
            
            const totalCostValue = activeProducts.reduce((sum, product) => 
                sum + (product.quantity_in_stock * product.cost_price), 0
            );

            return {
                totalProducts: products.length,
                activeProducts: activeProducts.length,
                totalCategories: categories.length,
                totalSuppliers: suppliers.length,
                lowStockProducts: lowStockProducts.length,
                outOfStockProducts: outOfStockProducts.length,
                totalInventoryValue: totalValue,
                totalCostValue: totalCostValue,
                potentialProfit: totalValue - totalCostValue
            };
        } catch (error) {
            throw error;
        }
    }
};

export default inventoryService;